/**
 * Company Vehicle Form Page (Create/Edit)
 * Epic 34: Járműnyilvántartás (ADR-027)
 */

import type { CompanyVehicle } from '@/api/vehicles';
import { useVehicle, useVehicleMutations } from '@/hooks/use-vehicles';
import { useTranslation } from '@kgc/i18n';
import { ArrowLeft, Car, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type VehicleType = CompanyVehicle['type'];
type VehicleStatus = CompanyVehicle['status'];
type FuelType = CompanyVehicle['fuelType'];

interface VehicleFormData {
  licensePlate: string;
  type: VehicleType;
  name: string;
  brand: string;
  model: string;
  year: number;
  status: VehicleStatus;
  fuelType: FuelType;
  mileage: number;
  technicalExpiryDate: string;
  registrationExpiryDate: string;
  kgfbExpiryDate: string;
  cascoExpiryDate: string;
  highwayPassExpiryDate: string;
  assignedTenantId: string;
  notes: string;
}

const INITIAL_FORM_DATA: VehicleFormData = {
  licensePlate: '',
  type: 'car',
  name: '',
  brand: '',
  model: '',
  year: new Date().getFullYear(),
  status: 'active',
  fuelType: 'petrol',
  mileage: 0,
  technicalExpiryDate: '',
  registrationExpiryDate: '',
  kgfbExpiryDate: '',
  cascoExpiryDate: '',
  highwayPassExpiryDate: '',
  assignedTenantId: '',
  notes: '',
};

// TYPE_OPTIONS, FUEL_OPTIONS, FRANCHISE_LOCATIONS moved inside component for i18n support

export function CompanyVehicleFormPage() {
  const { t } = useTranslation();

  const TYPE_OPTIONS = [
    { value: 'car', label: t('vehicle.companyTypeLabels.car') },
    { value: 'van', label: t('vehicle.companyTypeLabels.van') },
    { value: 'truck', label: t('vehicle.companyTypeLabels.truck') },
  ];

  const FUEL_OPTIONS = [
    { value: 'petrol', label: t('vehicle.fuelLabels.petrol') },
    { value: 'diesel', label: t('vehicle.fuelLabels.diesel') },
    { value: 'electric', label: t('vehicle.fuelLabels.electric') },
    { value: 'hybrid', label: t('vehicle.fuelLabels.hybrid') },
  ];

  const FRANCHISE_LOCATIONS = [
    { id: '', name: t('vehicle.noAssignment') },
    { id: 't1', name: 'KGC Budapest' },
    { id: 't2', name: 'KGC Debrecen' },
    { id: 't3', name: 'KGC Szeged' },
  ];

  const STATUS_OPTIONS = [
    { value: 'active', label: t('vehicle.companyStatusLabels.active') },
    { value: 'in_use', label: t('vehicle.companyStatusLabels.in_use') },
    { value: 'maintenance', label: t('vehicle.companyStatusLabels.maintenance') },
    { value: 'inactive', label: t('vehicle.companyStatusLabels.inactive') },
  ];
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<VehicleFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Partial<Record<keyof VehicleFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { vehicle } = useVehicle(id);
  const { createCompanyVehicle, updateVehicle } = useVehicleMutations();

  useEffect(() => {
    if (isEditing && vehicle && vehicle.vehicleType === 'COMPANY') {
      const cv = vehicle as CompanyVehicle;
      setFormData({
        licensePlate: cv.licensePlate,
        type: cv.type,
        name: cv.name,
        brand: cv.brand,
        model: cv.model,
        year: cv.year,
        status: cv.status,
        fuelType: cv.fuelType,
        mileage: cv.mileage,
        technicalExpiryDate: cv.technicalExpiryDate,
        registrationExpiryDate: cv.registrationExpiryDate,
        kgfbExpiryDate: cv.kgfbExpiryDate,
        cascoExpiryDate: cv.cascoExpiryDate ?? '',
        highwayPassExpiryDate: cv.highwayPassExpiryDate ?? '',
        assignedTenantId: cv.assignedTenantId ?? '',
        notes: cv.notes ?? '',
      });
    }
  }, [isEditing, vehicle]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof VehicleFormData, string>> = {};

    if (!formData.licensePlate.trim()) {
      newErrors.licensePlate = t('vehicle.licensePlateRequired');
    }
    if (!formData.name.trim()) {
      newErrors.name = t('vehicle.nameRequired');
    }
    if (!formData.brand.trim()) {
      newErrors.brand = t('vehicle.brandRequired');
    }
    if (!formData.kgfbExpiryDate) {
      newErrors.kgfbExpiryDate = t('vehicle.kgfbExpiryRequired');
    }
    if (!formData.technicalExpiryDate) {
      newErrors.technicalExpiryDate = t('vehicle.technicalExpiryRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (isEditing && id) {
        await updateVehicle(id, formData, 'COMPANY');
      } else {
        await createCompanyVehicle({
          ...formData,
          cascoExpiryDate: formData.cascoExpiryDate || undefined,
          highwayPassExpiryDate: formData.highwayPassExpiryDate || undefined,
          assignedTenantId: formData.assignedTenantId || undefined,
        });
      }
      navigate('/vehicles/company');
    } catch (error) {
      console.error('Error saving vehicle:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof VehicleFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="kgc-bg min-h-screen p-6">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/vehicles/company')}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? t('vehicle.editCompanyVehicle') : t('vehicle.newCompanyVehicle')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditing ? t('vehicle.editVehicleData') : t('vehicle.registerNewCompanyVehicle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mx-auto max-w-4xl">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          {/* Basic Info */}
          <div className="mb-6 flex items-center gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
            <div className="rounded-lg bg-primary/10 p-2">
              <Car className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('vehicle.basicInfo')}
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.licensePlate')} *
              </label>
              <input
                type="text"
                name="licensePlate"
                value={formData.licensePlate}
                onChange={handleChange}
                placeholder="ABC-123"
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${errors.licensePlate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.licensePlate && (
                <p className="mt-1 text-sm text-red-500">{errors.licensePlate}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.typeLabel')} *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                {TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.statusLabel')} *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.designation')} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('vehicle.placeholder.companyName')}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.brandLabel')} *
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder={t('vehicle.placeholder.brand')}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${errors.brand ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.brand && <p className="mt-1 text-sm text-red-500">{errors.brand}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.modelLabel')}
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder={t('vehicle.placeholder.model')}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.yearOfManufacture')}
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min={1990}
                max={2030}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.fuelType')}
              </label>
              <select
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                {FUEL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.odometerReading')}
              </label>
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                min={0}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.assignedStore')}
              </label>
              <select
                name="assignedTenantId"
                value={formData.assignedTenantId}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                {FRANCHISE_LOCATIONS.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Expiry Dates */}
          <div className="mb-6 mt-8 border-b border-gray-200 pb-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('vehicle.expiryDatesAndInsurance')}
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.technicalInspection')} *
              </label>
              <input
                type="date"
                name="technicalExpiryDate"
                value={formData.technicalExpiryDate}
                onChange={handleChange}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${errors.technicalExpiryDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.technicalExpiryDate && (
                <p className="mt-1 text-sm text-red-500">{errors.technicalExpiryDate}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.registrationCert')}
              </label>
              <input
                type="date"
                name="registrationExpiryDate"
                value={formData.registrationExpiryDate}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.mtplInsurance')} *
              </label>
              <input
                type="date"
                name="kgfbExpiryDate"
                value={formData.kgfbExpiryDate}
                onChange={handleChange}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${errors.kgfbExpiryDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              />
              {errors.kgfbExpiryDate && (
                <p className="mt-1 text-sm text-red-500">{errors.kgfbExpiryDate}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.cascoInsurance')}
              </label>
              <input
                type="date"
                name="cascoExpiryDate"
                value={formData.cascoExpiryDate}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('vehicle.highwaySticker')}
              </label>
              <input
                type="date"
                name="highwayPassExpiryDate"
                value={formData.highwayPassExpiryDate}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="mt-8">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('common.notes')}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              placeholder={t('vehicle.additionalInfo')}
            />
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/vehicles/company')}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? t('vehicle.savingInProgress') : t('common.save')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
